import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const ProducerAuthContext = createContext(null);

// Default permissions for main admin (all true)
const ALL_PERMISSIONS = {
  manage_events: true,
  manage_tickets: true,
  manage_validators: true,
  manage_rrpp: true,
  manage_invitations: true,
  view_reports: true,
  manage_settlements: true,
  scan_tickets: true,
  manage_admins: true
};

// No permissions - used while loading
const NO_PERMISSIONS = {
  manage_events: false,
  manage_tickets: false,
  manage_validators: false,
  manage_rrpp: false,
  manage_invitations: false,
  view_reports: false,
  manage_settlements: false,
  scan_tickets: false,
  manage_admins: false
};

export const ProducerAuthProvider = ({ children }) => {
  const [producer, setProducer] = useState(null);
  // Start with null to indicate "not yet loaded"
  const [permissions, setPermissions] = useState(null);
  const [isSecondaryAdmin, setIsSecondaryAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('producer_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Set token in headers
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const [producerRes, subRes] = await Promise.all([
        api.get('/producers/me'),
        api.get('/producers/me/subscription').catch(() => ({ data: null }))
      ]);
      
      setProducer(producerRes.data);
      setSubscription(subRes.data);
      
      // Set permissions from response
      if (producerRes.data.admin_permissions) {
        setPermissions(producerRes.data.admin_permissions);
      } else {
        setPermissions(ALL_PERMISSIONS);
      }
      setIsSecondaryAdmin(producerRes.data.is_secondary_admin || false);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('producer_token');
      localStorage.removeItem('producer_data');
    } finally {
      setLoading(false);
    }
  };

  const login = (token, producerData) => {
    localStorage.setItem('producer_token', token);
    localStorage.setItem('producer_data', JSON.stringify(producerData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setProducer(producerData);
    
    // Set permissions from login data
    if (producerData.permissions) {
      setPermissions(producerData.permissions);
      setIsSecondaryAdmin(true);
    } else if (producerData.admin_permissions) {
      setPermissions(producerData.admin_permissions);
      setIsSecondaryAdmin(producerData.is_secondary_admin || false);
    } else {
      setPermissions(ALL_PERMISSIONS);
      setIsSecondaryAdmin(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('producer_token');
    localStorage.removeItem('producer_data');
    delete api.defaults.headers.common['Authorization'];
    setProducer(null);
    setPermissions(ALL_PERMISSIONS);
    setIsSecondaryAdmin(false);
    navigate('/productoras/login');
  };

  const refreshProducer = async () => {
    try {
      const [producerRes, subRes] = await Promise.all([
        api.get('/producers/me'),
        api.get('/producers/me/subscription').catch(() => ({ data: null }))
      ]);
      
      setProducer(producerRes.data);
      setSubscription(subRes.data);
      
      if (producerRes.data.admin_permissions) {
        setPermissions(producerRes.data.admin_permissions);
      }
      setIsSecondaryAdmin(producerRes.data.is_secondary_admin || false);
      return producerRes.data;
    } catch (error) {
      console.error('Refresh failed:', error);
      return null;
    }
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    // If permissions haven't loaded yet, be restrictive
    if (permissions === null) return false;
    // Main admin always has all permissions
    if (!isSecondaryAdmin) return true;
    return permissions[permission] === true;
  };

  // Check if user has any of the given permissions
  const hasAnyPermission = (permissionList) => {
    // If permissions haven't loaded yet, be restrictive
    if (permissions === null) return false;
    if (!isSecondaryAdmin) return true;
    return permissionList.some(p => permissions[p] === true);
  };

  // Subscription helpers
  const isPremium = () => {
    return subscription?.subscription?.is_premium === true;
  };

  const getFeatureLimits = () => {
    return subscription?.subscription?.features || {
      max_venues: 1,
      allowed_styles: ['classic'],
      max_job_posts: 1,
      monthly_emails: 0,
      can_duplicate_events: false,
      can_duplicate_venues: false,
      can_request_early_settlement: false,
      service_fee_percent: 15.0
    };
  };

  const canUseStyle = (styleId) => {
    const features = getFeatureLimits();
    return features.allowed_styles?.includes(styleId) || false;
  };

  const canUseBackground = (bgId) => {
    // 'none' is the free default. Any other background requires Super Productora.
    if (!bgId || bgId === 'none') return true;
    return isPremium();
  };

  const canUseCanvasShape = (shapeId) => {
    const features = getFeatureLimits();
    const allowed = features.allowed_canvas_shapes || ['rectangle'];
    return allowed.includes(shapeId);
  };

  const getPaymentLink = () => {
    return subscription?.plan_info?.rebill_link || '';
  };

  return (
    <ProducerAuthContext.Provider value={{
      producer,
      loading,
      isAuthenticated: !!producer,
      permissions,
      isSecondaryAdmin,
      subscription,
      isPremium,
      getFeatureLimits,
      canUseStyle,
      canUseBackground,
      canUseCanvasShape,
      getPaymentLink,
      hasPermission,
      hasAnyPermission,
      login,
      logout,
      refreshProducer
    }}>
      {children}
    </ProducerAuthContext.Provider>
  );
};

export const useProducerAuth = () => {
  const context = useContext(ProducerAuthContext);
  if (!context) {
    throw new Error('useProducerAuth must be used within a ProducerAuthProvider');
  }
  return context;
};

// Protected route component
export const ProducerProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useProducerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/productoras/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};

export default ProducerAuthContext;
